import { Component, Element, Event, EventEmitter, h, Listen, Method, Prop, State, Watch } from '@stencil/core';

import { KEYBOARD } from '../../../config/constants';
import type { FloatingUIOptions } from '../../../services/interfaces';
import { FloatingUIPlacement } from '../../../services/interfaces';
import { FloatingUI } from '../../../services/libraries';
import { isMobile } from '../../../services/utils';

let dropdownId = 0;

@Component({
  tag: 'pk-dropdown',
  styleUrl: './scss/pk-dropdown.scss',
  shadow: true,
})
export class PkDropdown {
  // Own Properties
  // ====================

  private id = `dropdown-${++dropdownId}`;
  private panel: HTMLElement;
  private arrowElement: HTMLElement;
  private trigger: HTMLElement;
  private floatingUI: FloatingUI;
  private leaveTimer: NodeJS.Timeout;
  private enterTimer: NodeJS.Timeout;

  // Reference to host HTML element
  // ===================================

  @Element() el!: HTMLPkDropdownElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  @State() currentPosition: FloatingUIOptions['placement'];
  @State() panelOffset: number = 0;

  // Public Property API
  // ========================

  /** Determines whether the dropdown should hide when a menu item is selected. */
  @Prop({ reflect: true }) closeOnSelect = true;

  /** The dropdown will close when the user interacts outside of this element (e.g. clicking). */
  @Prop() containingElement: HTMLElement = this.el;

  /** The distance in pixels from which to offset the panel away from its trigger. */
  @Prop({ reflect: true }) distance = 20;

  /** If true, the arrow element won't be shown when the panel drops */
  @Prop({ reflect: true }) hideArrow: boolean = false;

  /** It specifies the size of the arrow element (default is the same as `distance`). */
  @Prop({ reflect: true }) arrowSize?: number = this.distance;

  /** Indicates whether or not the dropdown is open. You can use this in lieu of the show/hide methods. */
  @Prop({ reflect: true, mutable: true }) open = false;

  /** Disables the dropdown opening */
  @Prop() disabled = false;

  /** It will set when the dropdown will be shown, on hover or click (default)  */
  @Prop({ reflect: true }) showOn?: 'click' | 'hover' = 'click';

  /** It will set the height of the panel container that opens */
  @Prop({ reflect: true }) panelHeight: string;

  /**
   * The preferred placement of the dropdown panel. Note that the actual placement may vary as needed to keep the panel
   * inside of the viewport.
   */
  @Prop() placement: FloatingUIPlacement = 'bottom-start';

  /** If true, the dropdown will have same width as its reference (or trigger) element */
  @Prop() sameWidth: boolean = false;

  /** The distance in pixels from which to offset the panel along its trigger. */
  @Prop() skidding = 0;

  /** The delay before the hover events are triggered */
  @Prop() hoverDelay = 200;

  // Prop lifecycle events
  // =======================

  @Watch('open')
  handleOpenChange() {
    if (!this.open) {
      this.hide();
      return;
    }

    this.show();
  }

  @Watch('placement')
  @Watch('hideArrow')
  @Watch('arrowSize')
  @Watch('distance')
  @Watch('sameWidth')
  @Watch('skidding')
  handleFloatingUIOptionsChange() {
    this.floatingUI?.init(this.floatingUIOptions);
  }

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  /** Callback handler to be called when the dropdown opens. Calling `event.preventDefault()` will prevent it from being opened. */
  @Event() pkShow: EventEmitter;

  /** Callback handler to be called after the dropdown opens and all transitions are complete. */
  @Event() pkAfterShow: EventEmitter;

  /** Callback handler to be called when the dropdown closes. Calling `event.preventDefault()` will prevent it from being closed. */
  @Event() pkHide: EventEmitter;

  /** Callback handler to be called after the dropdown closes and all transitions are complete. */
  @Event() pkAfterHide: EventEmitter;

  /** Callback handler to be called when the dropdown panel position change. */
  @Event() pkPositionChange: EventEmitter<{ state: { placement: FloatingUIPlacement } }>;

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  componentDidLoad() {
    this.floatingUI = new FloatingUI(this.trigger, this.panel, this.floatingUIOptions);

    // Show on init if open
    if (this.open) {
      this.show();
    }
  }

  disconnectedCallback() {
    this.hide();
  }

  // Listeners
  // ==============

  @Listen('scroll', { target: 'window' })
  handleBodyScroll() {
    if (!isMobile()) return;
    this.hide();
  }

  @Listen('mousedown', { target: 'document' })
  handleDocumentMouseDown(event: MouseEvent) {
    if (!this.open) return;
    // Close when clicking outside of the close element
    const path = event.composedPath();
    if (!path.includes(this.containingElement)) {
      this.hide();
    }
  }

  @Listen('keydown', { target: 'document' })
  handleDocumentKeyDown(event: KeyboardEvent) {
    switch (event.key) {
      case KEYBOARD.Escape:
        if (!this.open) return;
        // Close when escape is pressed
        this.hide();
        this.focusOnTrigger();
        break;
      case KEYBOARD.Tab:
        // Close when tabbing results in the focus leaving the containing element
        setTimeout(() => {
          if (
            document.activeElement &&
            document.activeElement.closest(this.containingElement.tagName.toLowerCase()) !== this.containingElement
          ) {
            this.hide();
          }
        });
        break;
      default:
        // Prevent scrolling when certain keys are pressed
        if ([KEYBOARD.ArrowDown, KEYBOARD.ArrowUp, 'Home', 'End'].includes(event.key)) {
          event.preventDefault();
        }
        // If a menu is present, focus on it when certain keys are pressed
        const menu = this.getMenu();
        if (menu && ([KEYBOARD.ArrowDown, KEYBOARD.ArrowUp] as string[]).includes(event.key)) {
          event.preventDefault();
          menu.setFocus();
        }
        break;
    }
  }

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  /** Shows the dropdown panel */
  @Method()
  async show() {
    if (!this.getMenu() || this.disabled) return;

    this.open = true;

    const dsShow = this.pkShow.emit();
    if (dsShow.defaultPrevented) {
      this.open = false;
      return;
    }

    this.showPanel();
    this.panel.addEventListener('pkSelect', this.handlePanelSelect);
  }

  /** Hides the dropdown panel */
  @Method()
  async hide() {
    this.open = false;

    const dsHide = this.pkHide.emit();
    if (dsHide.defaultPrevented) {
      this.open = true;
      return;
    }

    this.hidePanel();
    this.panel?.removeEventListener('pkSelect', this.handlePanelSelect);
  }

  /** Refresh the dropdown placement position */
  @Method()
  async refreshPosition() {
    this.handleFloatingUIOptionsChange();
  }

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  private get floatingUIOptions(): FloatingUIOptions {
    return {
      placement: this.placement,
      distance: !this.hideArrow ? this.distance : this.distance - 10,
      arrow: !this.hideArrow ? this.arrowElement : null,
      arrowSize: this.arrowSize,
      strategy: 'fixed',
      sameWidth: this.sameWidth,
      skidding: this.skidding,
      onPositionChange: this.handlePositionChange,
    };
  }

  private focusOnTrigger = () => {
    const slot = this.trigger.querySelector('slot');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const trigger = slot.assignedElements({ flatten: true })[0] as any;
    if (trigger) {
      if (typeof trigger.setFocus === 'function') {
        trigger.setFocus();
      } else if (typeof trigger.focus === 'function') {
        trigger.focus();
      }
    }
  };

  private getMenu = () => {
    if (!this.panel) return;

    return this.panel
      .querySelector('slot')
      .assignedElements({ flatten: true })
      .filter((el) => el.tagName.toLowerCase() === 'pk-menu')[0] as HTMLPkMenuElement;
  };

  private handlePanelSelect = (event: CustomEvent) => {
    const target = event.target as HTMLElement;
    this.focusOnTrigger();
    // Hide the dropdown when a menu item is selected
    if (this.closeOnSelect && target.tagName.toLowerCase() === 'pk-menu') {
      this.hide();
    }
  };

  private handleTriggerKeyDown = (event: KeyboardEvent) => {
    // Open the panel when pressing down or up while focused on the trigger
    if (!this.open && ([KEYBOARD.ArrowDown, KEYBOARD.ArrowUp] as string[]).includes(event.key)) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.show();
  };

  private handlePositionChange = (placement: FloatingUIPlacement) => {
    if (placement === this.currentPosition) return;

    this.currentPosition = placement;
    /**
     * We add a custom attribute to the element host
     * to provide a way for consumers to target position changes also from CSS.
     * E.g: ds-dropdown[data-panel-position="top-start"] { ... }
     */
    this.el.setAttribute('data-panel-position', this.currentPosition);
    /**
     * Emit and event whenever the position changes
     * in case there's a need to trigger some JavaScript code upon changes.
     */
    this.pkPositionChange.emit({ state: { placement } });
  };

  private togglePanel = () => {
    this.open ? this.hide() : this.show();
  };

  private handleTriggerClick = () => {
    if (this.showOn !== 'click') return;
    this.togglePanel();
  };

  private handleMouseEnter = () => {
    if (this.showOn !== 'hover') return;

    clearTimeout(this.leaveTimer);
    this.enterTimer = setTimeout(() => {
      this.show();
    }, this.hoverDelay);
  };

  private handleMouseLeave = () => {
    if (this.showOn !== 'hover') return;

    clearTimeout(this.enterTimer);
    this.leaveTimer = setTimeout(() => {
      this.hide();
    }, this.hoverDelay);
  };

  private showPanel() {
    if (!this.panel) return;

    this.panel.style.display = 'block';
    this.floatingUI?.update();
  }

  private async hidePanel() {
    if (!this.panel) return;
    this.panel.style.display = 'none';

    // Tell the `PkMenu` child to collapse all expanded items
    // that has submenu :)
    const pkMenu = this.el.querySelector('pk-menu');
    if (!pkMenu) return;

    await pkMenu.collapseAll();
  }

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    const style = {
      ...(this.panelHeight && { '--dropdown--height': this.panelHeight }),
      ...(this.panelHeight && { '--dropdown--v-offset': `${this.panelOffset}px` }),
      '--dropdown--arrow-size': `${this.arrowSize}px`,
    };
    return (
      <div
        id={this.id}
        class={{
          'pk-dropdown relative': true,
          'is-open': this.open,
        }}
        style={{ ...style }}
        aria-haspopup="true"
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
        part="base"
      >
        {/* Dropdown trigger */}
        <button
          type="button"
          part="trigger"
          class="button-reset block"
          ref={(el) => (this.trigger = el)}
          onKeyDown={this.handleTriggerKeyDown}
          onClick={this.handleTriggerClick}
        >
          <slot name="trigger" />
        </button>
        {/* Dropdown panel */}
        <div
          ref={(el) => (this.panel = el)}
          part="menu"
          class="pk-dropdown__panel"
          aria-hidden={!this.open}
          hidden={!this.open}
        >
          {!this.hideArrow && (
            <div id="arrow" class="arrow" ref={(el: HTMLElement) => (this.arrowElement = el)} part="arrow" />
          )}
          <slot />
        </div>
      </div>
    );
  }
}
